import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import axiosInstance from '../../utils/axiosInstance';
import * as XLSX from 'xlsx';

const PageWrapper = styled.div`
  width: 100%;
  min-height: 100vh;
  background: #f8fafc;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
`;

const PageContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 40px;
  flex: 1;
`;

const BackButton = styled.button`
  align-self: flex-start;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 10px 16px;
  background: #ffffff;
  color: #334155;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &:hover {
    border-color: #94a3b8;
    background: #f8fafc;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 28px;
  font-weight: 800;
  color: #0f172a;
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 16px;
  color: #475569;
  font-weight: 500;
`;

const AnalysisCard = styled.section`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
`;

const AnalysisContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
  padding-bottom: 12px;
  border-bottom: 2px solid #e2e8f0;
`;

const Placeholder = styled.div`
  padding: 48px 24px;
  text-align: center;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px dashed #cbd5e1;
  color: #64748b;
  font-size: 15px;
`;

const PeriodInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const InfoCard = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const InfoLabel = styled.span`
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #64748b;
`;

const InfoValue = styled.span`
  font-size: 24px;
  font-weight: 800;
  color: #0f172a;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  width: 100%;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 500px;
  border: 1px solid #d1d5db;
`;

const Th = styled.th`
  text-align: ${(p) => (p.$alignRight ? 'right' : 'left')};
  padding: 14px 16px;
  background: #f8fafc;
  border: 1px solid #d1d5db;
  color: #475569;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 14px 16px;
  border: 1px solid #d1d5db;
  color: #0f172a;
  font-size: 14px;
  text-align: ${(p) => (p.$alignRight ? 'right' : 'left')};
  vertical-align: middle;
`;

const Tr = styled.tr`
  background: #ffffff;
`;

const DashboardLoader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 280px;
  border: 1px dashed #cbd5e1;
  border-radius: 14px;
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
`;

const LoaderSpinner = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 4px solid #dbeafe;
  border-top-color: #0284c7;
  animation: analysis-spin 0.8s linear infinite;

  @keyframes analysis-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoaderLabel = styled.p`
  margin: 0;
  color: #475569;
  font-size: 14px;
  font-weight: 600;
`;

const ErrorText = styled.div`
  padding: 32px 24px;
  text-align: center;
  color: #b91c1c;
  font-size: 15px;
  background: #fef2f2;
  border-radius: 12px;
  border: 1px solid #fecaca;
`;

const SectionHeadRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  flex-wrap: wrap;
`;

const DownloadButton = styled.button`
  border: 1px solid #0ea5e9;
  border-radius: 10px;
  padding: 10px 14px;
  background: #ffffff;
  color: #0369a1;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background: #f0f9ff;
    border-color: #0284c7;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const MONTH_NAMES = {
  1: 'January',
  2: 'February',
  3: 'March',
  4: 'April',
  5: 'May',
  6: 'June',
  7: 'July',
  8: 'August',
  9: 'September',
  10: 'October',
  11: 'November',
  12: 'December',
};

export default function BranchNewIncomeAnalysis() {
  const [searchParams] = useSearchParams();
  const [courseData, setCourseData] = useState([]);
  const [counselorData, setCounselorData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    // Set full page background
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.body.style.backgroundColor = '#f8fafc';
    
    return () => {
      document.body.style.margin = '';
      document.body.style.padding = '';
      document.body.style.backgroundColor = '';
    };
  }, []);

  const year = Number(searchParams.get('year')) || new Date().getFullYear();
  const month = Number(searchParams.get('month')) || new Date().getMonth() + 1;

  useEffect(() => {
    let ignore = false;

    const fetchAnalysisData = async () => {
      try {
        setLoading(true);
        setError('');
        
        const response = await axiosInstance.get('/admin/branch-new-income', {
          params: { year, month }
        });

        if (ignore) return;

        // Calculate course-wise stats with unique registrations
        const courseMap = new Map();
        const registrationsByCourse = new Map();
        const counselorCourseMap = new Map();
        const counselorTotals = new Map();

        if (Array.isArray(response.data?.data)) {
          response.data.data.forEach((row) => {
            const courseName = row.course || 'Unknown';
            const registrationId = row.registrationId;
            const amount = Number(row.amount) || 0;
            const counselorName = row.resolvedCounselorName || row.registeredCounselorName || 'Unassigned';

            // Track unique registrations per course
            if (!registrationsByCourse.has(courseName)) {
              registrationsByCourse.set(courseName, new Set());
            }
            if (registrationId) {
              registrationsByCourse.get(courseName).add(registrationId);
            }

            // Aggregate income by course
            if (!courseMap.has(courseName)) {
              courseMap.set(courseName, { course: courseName, income: 0, registrationCount: 0 });
            }
            const courseEntry = courseMap.get(courseName);
            courseEntry.income += amount;

            // Aggregate counselor + course rows with unique registration count
            const counselorCourseKey = `${counselorName}__${courseName}`;
            if (!counselorCourseMap.has(counselorCourseKey)) {
              counselorCourseMap.set(counselorCourseKey, {
                counselor: counselorName,
                course: courseName,
                registrationIds: new Set(),
                amount: 0,
              });
            }

            const counselorCourseEntry = counselorCourseMap.get(counselorCourseKey);
            counselorCourseEntry.amount += amount;
            if (registrationId) {
              counselorCourseEntry.registrationIds.add(registrationId);
            }

            if (!counselorTotals.has(counselorName)) {
              counselorTotals.set(counselorName, 0);
            }
            counselorTotals.set(counselorName, counselorTotals.get(counselorName) + amount);
          });
        }

        // Build final course data with unique registration counts
        const finalCourseData = Array.from(courseMap.values()).map((entry) => ({
          ...entry,
          registrationCount: registrationsByCourse.get(entry.course)?.size || 0,
        }));

        // Sort by income descending
        finalCourseData.sort((a, b) => b.income - a.income);

        const finalCounselorData = Array.from(counselorCourseMap.values()).map((entry) => ({
          counselor: entry.counselor,
          course: entry.course,
          count: entry.registrationIds.size,
          amount: entry.amount,
          counselorTotalAmount: counselorTotals.get(entry.counselor) || 0,
        }));

        finalCounselorData.sort((a, b) => {
          const aUnassigned = String(a.counselor || '').trim().toLowerCase() === 'unassigned';
          const bUnassigned = String(b.counselor || '').trim().toLowerCase() === 'unassigned';
          if (aUnassigned !== bUnassigned) return aUnassigned ? 1 : -1;

          const totalDiff = (b.counselorTotalAmount || 0) - (a.counselorTotalAmount || 0);
          if (totalDiff !== 0) return totalDiff;

          const counselorDiff = String(a.counselor || '').localeCompare(String(b.counselor || ''));
          if (counselorDiff !== 0) return counselorDiff;

          const countDiff = (b.count || 0) - (a.count || 0);
          if (countDiff !== 0) return countDiff;

          const amountDiff = (b.amount || 0) - (a.amount || 0);
          if (amountDiff !== 0) return amountDiff;

          return String(a.course || '').localeCompare(String(b.course || ''));
        });

        setCourseData(finalCourseData);
        setCounselorData(finalCounselorData);
      } catch (err) {
        if (!ignore) {
          setError(err?.response?.data?.error || 'Failed to load analysis data.');
          setCourseData([]);
          setCounselorData([]);
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    fetchAnalysisData();
    return () => {
      ignore = true;
    };
  }, [year, month]);

  const monthName = MONTH_NAMES[month] || 'Unknown';
  const formalDate = useMemo(() => {
    if (month >= 1 && month <= 12) {
      return `${monthName} ${year}`;
    }
    return `${year}`;
  }, [month, year, monthName]);

  const totals = useMemo(() => {
    return {
      courses: courseData.length,
      totalRegistrations: courseData.reduce((sum, c) => sum + c.registrationCount, 0),
      totalIncome: courseData.reduce((sum, c) => sum + c.income, 0),
    };
  }, [courseData]);

  const sortedCourseData = useMemo(() => {
    return [...courseData].sort((a, b) => {
      const countDiff = (Number(b.registrationCount) || 0) - (Number(a.registrationCount) || 0);
      if (countDiff !== 0) return countDiff;

      const incomeDiff = (Number(b.income) || 0) - (Number(a.income) || 0);
      if (incomeDiff !== 0) return incomeDiff;

      return String(a.course || '').localeCompare(String(b.course || ''));
    });
  }, [courseData]);

  const sortedCounselorData = useMemo(() => {
    return [...counselorData].sort((a, b) => {
      const aUnassigned = String(a.counselor || '').trim().toLowerCase() === 'unassigned';
      const bUnassigned = String(b.counselor || '').trim().toLowerCase() === 'unassigned';
      if (aUnassigned !== bUnassigned) return aUnassigned ? 1 : -1;

      const totalDiff = (Number(b.counselorTotalAmount) || 0) - (Number(a.counselorTotalAmount) || 0);
      if (totalDiff !== 0) return totalDiff;

      const counselorDiff = String(a.counselor || '').localeCompare(String(b.counselor || ''));
      if (counselorDiff !== 0) return counselorDiff;

      const countDiff = (Number(b.count) || 0) - (Number(a.count) || 0);
      if (countDiff !== 0) return countDiff;

      const amountDiff = (Number(b.amount) || 0) - (Number(a.amount) || 0);
      if (amountDiff !== 0) return amountDiff;

      return String(a.course || '').localeCompare(String(b.course || ''));
    });
  }, [counselorData]);

  const counselorMergedRows = useMemo(() => {
    const rows = [];
    let index = 0;

    while (index < sortedCounselorData.length) {
      const counselor = sortedCounselorData[index]?.counselor || 'Unassigned';
      let end = index;

      while (end < sortedCounselorData.length && (sortedCounselorData[end]?.counselor || 'Unassigned') === counselor) {
        end += 1;
      }

      const rowSpan = end - index;
      for (let i = index; i < end; i += 1) {
        rows.push({
          ...sortedCounselorData[i],
          showCounselor: i === index,
          isLastInGroup: i === end - 1,
          rowSpan,
        });
      }

      index = end;
    }

    return rows;
  }, [sortedCounselorData]);

  const formatAmount = (value) => {
    const amount = Number(value) || 0;
    return `Rs ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handleDownloadExcel = () => {
    if (!sortedCourseData.length) {
      return;
    }

    const rows = sortedCourseData.map((item, index) => ({
      No: index + 1,
      'Course Name': item.course || 'Unknown',
      'Unique Registrations': Number(item.registrationCount) || 0,
      'Total Income': Number(item.income) || 0,
    }));

    rows.push({
      No: '',
      'Course Name': 'TOTAL',
      'Unique Registrations': Number(totals.totalRegistrations) || 0,
      'Total Income': Number(totals.totalIncome) || 0,
    });

    const sheet = XLSX.utils.json_to_sheet(rows);
    sheet['!cols'] = [
      { wch: 8 },
      { wch: 42 },
      { wch: 22 },
      { wch: 20 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, 'Course Analysis');

    const monthToken = String(month).padStart(2, '0');
    const fileName = `course-wise-analysis-${year}-${monthToken}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  const handleDownloadCounselorExcel = () => {
    if (!counselorMergedRows.length) {
      return;
    }

    const rows = [];
    let serialNo = 1;

    counselorMergedRows.forEach((item) => {
      rows.push({
        No: serialNo,
        Counselor: item.counselor,
        Course: item.course,
        Count: Number(item.count) || 0,
        Amount: Number(item.amount) || 0,
      });

      serialNo += 1;

      if (item.isLastInGroup) {
        rows.push({
          No: '',
          Counselor: `${item.counselor} Total`,
          Course: '',
          Count: '',
          Amount: Number(item.counselorTotalAmount) || 0,
        });
      }
    });

    rows.push({
      No: '',
      Counselor: 'TOTAL INCOME',
      Course: '',
      Count: '',
      Amount: Number(totals.totalIncome) || 0,
    });

    const sheet = XLSX.utils.json_to_sheet(rows);
    sheet['!cols'] = [
      { wch: 8 },
      { wch: 30 },
      { wch: 32 },
      { wch: 12 },
      { wch: 18 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, 'Counselor Analysis');

    const monthToken = String(month).padStart(2, '0');
    const fileName = `counselor-wise-analysis-${year}-${monthToken}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  return (
    <PageWrapper>
      <PageContainer>
        <BackButton onClick={() => window.history.back()}>
          ← Back to New Income
        </BackButton>

        <Header>
          <Title>Analysis Dashboard</Title>
          <Subtitle>Comprehensive analysis for {formalDate}</Subtitle>
        </Header>

        <AnalysisCard>
          <AnalysisContent>
            <PeriodInfo>
              <InfoCard>
                <InfoLabel>Analysis Month</InfoLabel>
                <InfoValue>{monthName}</InfoValue>
              </InfoCard>
              <InfoCard>
                <InfoLabel>Analysis Year</InfoLabel>
                <InfoValue>{year}</InfoValue>
              </InfoCard>
              <InfoCard>
                <InfoLabel>Formal Period</InfoLabel>
                <InfoValue>{formalDate}</InfoValue>
              </InfoCard>
            </PeriodInfo>

            {loading ? (
              <DashboardLoader>
                <LoaderSpinner aria-hidden="true" />
                <LoaderLabel>Loading analysis dashboard...</LoaderLabel>
              </DashboardLoader>
            ) : (
              <>
                <div>
                  <SectionHeadRow>
                    <SectionTitle>Course-wise Registration Count & Income</SectionTitle>
                    <DownloadButton
                      type="button"
                      onClick={handleDownloadExcel}
                      disabled={Boolean(error) || sortedCourseData.length === 0}
                    >
                      Download Excel
                    </DownloadButton>
                  </SectionHeadRow>
                  {error ? (
                    <ErrorText>{error}</ErrorText>
                  ) : sortedCourseData.length === 0 ? (
                    <Placeholder>No income data available for {formalDate}.</Placeholder>
                  ) : (
                    <TableWrapper>
                      <Table>
                        <thead>
                          <tr>
                            <Th>No.</Th>
                            <Th>Course Name</Th>
                            <Th $alignRight>Unique Registrations</Th>
                            <Th $alignRight>Total Income</Th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedCourseData.map((item, idx) => (
                            <Tr key={item.course || idx}>
                              <Td>{idx + 1}</Td>
                              <Td>{item.course}</Td>
                              <Td $alignRight>{item.registrationCount}</Td>
                              <Td $alignRight style={{ fontWeight: '600', color: '#047857' }}>
                                {formatAmount(item.income)}
                              </Td>
                            </Tr>
                          ))}
                          <Tr style={{ backgroundColor: '#f8fafc', fontWeight: '700' }}>
                            <Td colSpan="2" style={{ fontWeight: '700' }}>TOTAL</Td>
                            <Td $alignRight style={{ fontWeight: '700', color: '#1e40af' }}>
                              {totals.totalRegistrations}
                            </Td>
                            <Td $alignRight style={{ fontWeight: '700', color: '#047857' }}>
                              {formatAmount(totals.totalIncome)}
                            </Td>
                          </Tr>
                        </tbody>
                      </Table>
                    </TableWrapper>
                  )}
                </div>

                <div>
                  <SectionHeadRow>
                    <SectionTitle>Counselor-wise Course Count & Amount</SectionTitle>
                    <DownloadButton
                      type="button"
                      onClick={handleDownloadCounselorExcel}
                      disabled={Boolean(error) || counselorMergedRows.length === 0}
                    >
                      Download Excel
                    </DownloadButton>
                  </SectionHeadRow>
                  {error ? (
                    <ErrorText>{error}</ErrorText>
                  ) : counselorMergedRows.length === 0 ? (
                    <Placeholder>No counselor data available for {formalDate}.</Placeholder>
                  ) : (
                    <TableWrapper>
                      <Table>
                        <thead>
                          <tr>
                            <Th>No.</Th>
                            <Th>Counselor</Th>
                            <Th>Course</Th>
                            <Th $alignRight>Count</Th>
                            <Th $alignRight>Amount</Th>
                          </tr>
                        </thead>
                        <tbody>
                          {counselorMergedRows.map((item, idx) => (
                            <React.Fragment key={`${item.counselor}-${item.course}-${idx}`}>
                              <Tr>
                                <Td>{idx + 1}</Td>
                                {item.showCounselor ? (
                                  <Td rowSpan={item.rowSpan} style={{ fontWeight: 700, verticalAlign: 'middle' }}>
                                    {item.counselor}
                                  </Td>
                                ) : null}
                                <Td>{item.course}</Td>
                                <Td $alignRight>{item.count}</Td>
                                <Td $alignRight style={{ fontWeight: '600', color: '#047857' }}>
                                  {formatAmount(item.amount)}
                                </Td>
                              </Tr>
                              {item.isLastInGroup ? (
                                <Tr style={{ backgroundColor: '#f8fafc', fontWeight: '700' }}>
                                  <Td colSpan="4" style={{ fontWeight: '700', color: '#334155' }}>
                                    {item.counselor} Total
                                  </Td>
                                  <Td $alignRight style={{ fontWeight: '700', color: '#0f766e' }}>
                                    {formatAmount(item.counselorTotalAmount)}
                                  </Td>
                                </Tr>
                              ) : null}
                            </React.Fragment>
                          ))}
                          <Tr style={{ backgroundColor: '#f8fafc', fontWeight: '700' }}>
                            <Td colSpan="4" style={{ fontWeight: '700' }}>TOTAL INCOME</Td>
                            <Td $alignRight style={{ fontWeight: '700', color: '#047857' }}>
                              {formatAmount(totals.totalIncome)}
                            </Td>
                          </Tr>
                        </tbody>
                      </Table>
                    </TableWrapper>
                  )}
                </div>
              </>
            )}

          </AnalysisContent>
        </AnalysisCard>
      </PageContainer>
    </PageWrapper>
  );
}
